import React, { useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { ScenarioType } from '../../types/app';
import { CalculatorParams } from '../../types/api';
import { ParameterGroup } from './ParameterGroup';
import { BUYING_PARAMETER_GROUPS, RENTING_PARAMETER_GROUPS } from '../../utils/constants';
import { setNestedValue, getNestedValue, sanitizeObject } from '../../utils/formatters';
import { dlog, debugEnabled } from '../../utils/debug';
import { useDebounce } from '../../hooks/useDebounce';

interface ParameterControlsProps {
  scenarioType: ScenarioType;
  timeHorizonYears: number;
  onTimeHorizonChange: (years: number) => void;
  parameters: CalculatorParams;
  onParametersChange: (parameters: CalculatorParams) => void;
  isAnalyzing?: boolean;
  scenarioTitle?: string;
  shouldPulse?: boolean;
}

export const ParameterControls: React.FC<ParameterControlsProps> = ({
  scenarioType,
  timeHorizonYears,
  onTimeHorizonChange,
  parameters,
  onParametersChange,
  isAnalyzing = false,
  scenarioTitle,
  shouldPulse = false,
}) => {
  // Local state for immediate UI updates
  const [localParameters, setLocalParameters] = useState<CalculatorParams>(parameters);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  // Update local parameters when external parameters change
  useEffect(() => {
    setLocalParameters(parameters);
    setHasLocalChanges(false);
  }, [parameters]);

  // Debounced parameters for actual analysis
  const debouncedParameters = useDebounce(localParameters, 500);

  // Ensure enhanced pulse animation styles are present when needed
  useEffect(() => {
    if (!shouldPulse) return;
    const styleId = 'enhanced-pulse-style';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes enhanced-pulse {
        0% {
          opacity: 1;
          transform: scale(1);
          box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.35);
        }
        50% {
          opacity: 0.92;
          transform: scale(1.015);
          box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.12);
        }
        100% {
          opacity: 1;
          transform: scale(1);
          box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.35);
        }
      }
      .enhanced-pulse {
        animation: enhanced-pulse 1.4s ease-in-out infinite;
      }
    `;
    document.head.appendChild(style);
  }, [shouldPulse]);

  // Trigger analysis when debounced parameters change
  useEffect(() => {
    // Only trigger if debounced parameters have stabilized AND differ from original
    const debouncedHash = JSON.stringify(debouncedParameters);
    const originalHash = JSON.stringify(parameters);
    
    if (debouncedHash !== originalHash && hasLocalChanges) {
      if (debugEnabled) {
        dlog('[ParamChange:Debounced] Dispatching update', {
          scenarioType,
          hasChanges: true,
          timestamp: Date.now(),
        });
      }
      onParametersChange(debouncedParameters);
      setHasLocalChanges(false);
    }
  }, [debouncedParameters, parameters, onParametersChange, scenarioType, hasLocalChanges]);

  // Handle individual parameter changes - immediate UI update
  const handleParameterChange = useCallback((key: string, value: number) => {
    try {
      // Sanitize parameters to remove any circular references or DOM elements
      const cleanParameters = sanitizeObject(localParameters);
      const updatedParameters = setNestedValue(cleanParameters, key, value);
      
      if (debugEnabled) {
        dlog('[ParamChange:Immediate]', {
          scenarioType,
          key,
          value,
          before: getNestedValue(localParameters as any, key),
          after: getNestedValue(updatedParameters as any, key),
        });
      }
      
      // Update local state immediately for UI feedback
      setLocalParameters(updatedParameters);
      setHasLocalChanges(true);
      
    } catch (error) {
      console.error('Error updating parameter:', key, value, error);
      // Fallback: try direct update
      try {
        const updatedParameters = setNestedValue({ ...localParameters }, key, value);
        if (debugEnabled) {
          dlog('[ParamChange:Fallback]', {
            scenarioType,
            key,
            value,
          });
        }
        setLocalParameters(updatedParameters);
        setHasLocalChanges(true);
      } catch (fallbackError) {
        console.error('Fallback parameter update also failed:', fallbackError);
      }
    }
  }, [localParameters, scenarioType]);

  // Get the appropriate parameter groups based on scenario type
  const parameterGroups = scenarioType === 'buying' ? BUYING_PARAMETER_GROUPS : RENTING_PARAMETER_GROUPS;

  return (
    <div
      className={classNames(
        'bg-gray-50 rounded-lg border border-gray-200 shadow-md p-4 overflow-visible transition-all duration-200',
        shouldPulse && 'enhanced-pulse border-green-400 shadow-lg'
      )}
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900 break-words">
          {scenarioTitle ? `Scenario Parameters: ${scenarioTitle}` : `${scenarioType === 'buying' ? 'Buying' : 'Renting'} Parameters`}
        </h2>

        {/* Scenario type badge */}
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
          scenarioType === 'buying'
            ? 'bg-green-100 text-green-800'
            : 'bg-blue-100 text-blue-800'
        }`}>
          {scenarioType === 'buying' ? 'Buying Scenario' : 'Renting Scenario'}
        </span>
      </div>

      {/* Years Slider moved to chart area in layout */}

      {/* Removed Save button (no-op previously) */}

      {/* Parameter Groups - arranged horizontally */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 custom-scrollbar">
        {parameterGroups.map((group) => (
          <ParameterGroup
            key={group.title}
            group={group}
            parameters={localParameters}
            onParameterChange={handleParameterChange}
            disabled={false}
          />
        ))}
      </div>

      {/* Analysis status indicator removed by request */}
    </div>
  );
};
